/**
 * NavigationContext
 *
 * Provides a way to navigate to specific fixtures from anywhere in the app.
 * Used by SearchModal and other components that need to trigger fixture navigation.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import type { FixtureLite } from '../types'
import type { PageId } from '../components'

// ============================================
// TYPES
// ============================================

// Minimal fixture data needed for navigation (from search results)
interface NavigationFixture {
  id: number
  api_fixture_id: number
  kickoff_utc: string
  status: string
  status_detail?: string | null
  home_score?: number | null
  away_score?: number | null
  round?: string | null
  venue?: string | null
  home_team: { id: number; name: string; crest_url?: string | null }
  away_team: { id: number; name: string; crest_url?: string | null }
}

interface FixtureNavigationRequest {
  type: 'fixture'
  fixture: NavigationFixture
  leagueApiId?: number
}

interface RefereeNavigationRequest {
  type: 'referee'
  refereeId: number
  leagueApiId?: number
}

type NavigationRequest = FixtureNavigationRequest | RefereeNavigationRequest

interface NavigationContextType {
  /** Request to navigate to a fixture (pass the fixture data directly) */
  navigateToFixture: (fixture: NavigationFixture, leagueApiId?: number) => void
  /** Request to navigate to a referee page (optionally in a specific league) */
  navigateToReferee: (refereeId: number, leagueApiId?: number) => void
  /** Current pending navigation request */
  pendingNavigation: NavigationRequest | null
  /** Clear the pending navigation after it's been handled */
  clearPendingNavigation: () => void
  /** Whether navigation is in progress - used to skip auto-select and reset */
  isNavigating: boolean
  /** ID of fixture that should be scrolled into view (set after navigation) */
  scrollToFixtureId: number | null
  /** ID of referee that should be scrolled into view (set after navigation) */
  scrollToRefereeId: number | null
  /** Clear the scroll-to state after scrolling is complete */
  clearScrollToFixture: () => void
  /** Clear the referee scroll-to state */
  clearScrollToReferee: () => void
}

// ============================================
// CONTEXT
// ============================================

const NavigationContext = createContext<NavigationContextType | null>(null)

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [pendingNavigation, setPendingNavigation] = useState<NavigationRequest | null>(null)
  // Use a ref for immediate access and state for re-renders
  const isNavigatingRef = useRef(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [scrollToFixtureId, setScrollToFixtureId] = useState<number | null>(null)
  const [scrollToRefereeId, setScrollToRefereeId] = useState<number | null>(null)

  const navigateToFixture = useCallback((fixture: NavigationFixture, leagueApiId?: number) => {
    isNavigatingRef.current = true
    setIsNavigating(true)
    setScrollToFixtureId(fixture.id) // Track which fixture to scroll to
    setPendingNavigation({
      type: 'fixture',
      fixture,
      leagueApiId,
    })
  }, [])

  const navigateToReferee = useCallback((refereeId: number, leagueApiId?: number) => {
    isNavigatingRef.current = true
    setIsNavigating(true)
    setScrollToRefereeId(refereeId) // Track which referee to scroll to
    setPendingNavigation({
      type: 'referee',
      refereeId,
      leagueApiId,
    })
  }, [])

  const clearPendingNavigation = useCallback(() => {
    setPendingNavigation(null)
    // Keep isNavigating true for 500ms to prevent race conditions with reset/auto-select
    setTimeout(() => {
      isNavigatingRef.current = false
      setIsNavigating(false)
    }, 500)
  }, [])

  const clearScrollToFixture = useCallback(() => {
    setScrollToFixtureId(null)
  }, [])

  const clearScrollToReferee = useCallback(() => {
    setScrollToRefereeId(null)
  }, [])

  return (
    <NavigationContext.Provider
      value={{
        navigateToFixture,
        navigateToReferee,
        pendingNavigation,
        clearPendingNavigation,
        isNavigating,
        scrollToFixtureId,
        scrollToRefereeId,
        clearScrollToFixture,
        clearScrollToReferee,
      }}
    >
      {children}
    </NavigationContext.Provider>
  )
}

export function useNavigation() {
  const context = useContext(NavigationContext)
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return context
}

/**
 * Hook to handle pending navigation requests.
 * Handles both fixture and referee navigation.
 */
export function useNavigationHandler(
  setSelected: (fixture: FixtureLite) => void,
  setActivePage: (page: PageId) => void,
  setLeague?: (leagueApiId: number) => void,
  onNavigationComplete?: (leagueApiId: number) => void
) {
  const { pendingNavigation, clearPendingNavigation } = useNavigation()
  const processedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!pendingNavigation) return

    // Create a unique key for this navigation request
    const navKey =
      pendingNavigation.type === 'fixture'
        ? `fixture-${pendingNavigation.fixture.id}`
        : `referee-${pendingNavigation.refereeId}`

    // Prevent processing the same navigation twice
    if (processedRef.current === navKey) return
    processedRef.current = navKey

    if (pendingNavigation.type === 'referee') {
      // Handle referee navigation
      const { leagueApiId } = pendingNavigation

      // Navigate to referees page
      setActivePage('referees' as PageId)

      // Note: We DON'T set selectedRefereeId here because that would open the detail page.
      // Instead, scrollToRefereeId (set in navigateToReferee) is used for highlighting in the list.

      // Switch league if needed
      if (leagueApiId && setLeague) {
        setLeague(leagueApiId)
      }

      // Notify completion
      if (leagueApiId && onNavigationComplete) {
        onNavigationComplete(leagueApiId)
      }

      // Clear navigation state
      clearPendingNavigation()

      // Reset processed ref after a delay
      setTimeout(() => {
        processedRef.current = null
      }, 1000)
      return
    }

    // Handle fixture navigation
    const { fixture, leagueApiId } = pendingNavigation

    // Navigate to fixtures page
    setActivePage('fixtures' as PageId)

    // Convert to FixtureLite format
    const fixtureLite: FixtureLite = {
      id: fixture.id,
      api_fixture_id: fixture.api_fixture_id,
      kickoff_utc: fixture.kickoff_utc,
      status: fixture.status,
      status_detail: fixture.status_detail,
      home_score: fixture.home_score,
      away_score: fixture.away_score,
      round: fixture.round,
      venue: fixture.venue,
      home_team: {
        id: fixture.home_team.id,
        name: fixture.home_team.name,
        crest_url: fixture.home_team.crest_url,
      },
      away_team: {
        id: fixture.away_team.id,
        name: fixture.away_team.name,
        crest_url: fixture.away_team.crest_url,
      },
      chips: [],
      events: [],
    }

    // Set fixture first, then switch league
    // This order ensures the fixture is set before any league-change effects run
    setSelected(fixtureLite)

    // Notify completion (updates lastAutoSelectLeagueRef)
    if (leagueApiId && onNavigationComplete) {
      onNavigationComplete(leagueApiId)
    }

    // Switch league if needed (do this AFTER setting the fixture)
    if (leagueApiId && setLeague) {
      setLeague(leagueApiId)
    }

    // Clear navigation state (keeps isNavigating true for 500ms)
    clearPendingNavigation()

    // Reset processed ref after a delay
    setTimeout(() => {
      processedRef.current = null
    }, 1000)
  }, [pendingNavigation, setSelected, setActivePage, setLeague, clearPendingNavigation, onNavigationComplete])
}
